import { FC, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button } from 'antd';
import PropTypes from 'prop-types';
import useBenguide from 'hooks/benguide';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  addComponentsToPage,
  deleteCustomPage,
  handleCustomPageImageRemoval,
  handleCustomPageSectionDeletion,
  handleGetAllCustomPageImageContent,
  isImageUrlCached,
  getCustomPageRichMediaContentAction,
  updateCustomPageRichMediaContentAction,
  CustomPage as CustomPageType,
  moveSectionUpWithAPI,
  moveSectionDownWithAPI,
  handleCustomPageImageUpload,
} from 'modules/customPages/slices/customPagesSlice';
import { updateBenguideLatestRevision } from 'modules/auth/slices/benguideSlice';
import AddCustomPageComponent from 'modules/benefitsWellness/components/AddCustomPageComponent/AddCustomPageComponent';
import RemoveCustomPageModal from 'modules/benefitsWellness/components/RemoveCustomPageModal/RemoveCustomPageModal';
import ContentCard from 'modules/customPages/components/ContentCard/ContentCard';
import TextComponent from 'modules/customPages/components/TextComponent/TextComponent';
import ImageComponent from 'modules/customPages/components/ImageComponent/ImageComponent';
import NoCustomPageSectionsView from 'components/NoCustomPageSectionsView/NoCustomectionsView';
import Spinner from 'components/Spinner/Spinner';
import title from 'images/icons/title-icon.svg';
import ErrorBoundary from 'modules/auth/pages/ErrorBoundary/ErrorBoundary';
import { getText } from 'utils/stringUtil';
import {
  CUSTOM_PAGE_NOT_FOUND,
  CUSTOM_PAGE_SECTION_NOT_FOUND,
  CUSTOM_PAGE_SECTION_NOT_FOUND_MORE_INFO,
} from 'constants/languageConstants';
import FooterTextSection from 'components/FooterTextSection';

import styles from './customPage.module.less';
// Use the CustomPage type from the slice
type CustomPageData = CustomPageType;

interface Props {
  pageData?: CustomPageData;
  customPagesList?: CustomPageData[];
}

const CustomPage: FC<Props> = ({ pageData }) => {
  const { customPageId, hash, employerName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const customPagesState = useAppSelector((state) => state.customPages);
  const { customPages } = customPagesState;
  const language = useAppSelector((state) => state.benguide.data?.language);
  const imageContentUrls = useAppSelector(
    (state) => state.customPages.imageContentUrls
  );

  // Extract previewToken from URL search parameters
  const search = location.search;
  const previewToken = new URLSearchParams(search).get('previewToken');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);

  const currentPageData =
    customPages.find((page) => page.id === customPageId) || pageData || null;

  const { isEditMode, masterId, latestRevision, revision } = useBenguide();

  // Helper function to get image URL from cache, trying different revisions if needed
  const getImageUrlFromCacheFlexible = (
    customPageId: string,
    componentId: string,
    original: boolean = false,
    preferredRevision?: number
  ): string => {
    const allCacheKeys = Object.keys(imageContentUrls);
    const componentCacheKeys = allCacheKeys.filter((key) =>
      key.includes(
        `${customPageId}-${componentId}-${original ? 'original' : 'cropped'}-`
      )
    );

    if (componentCacheKeys.length === 0) {
      return '';
    }

    // If we have a preferred revision, try that first
    if (preferredRevision) {
      const preferredKey = `${customPageId}-${componentId}-${
        original ? 'original' : 'cropped'
      }-${preferredRevision}`;
      if (imageContentUrls[preferredKey]) {
        return imageContentUrls[preferredKey];
      }
    }

    // Sort cache keys by revision number (highest first) to get the most recent image
    const sortedKeys = componentCacheKeys.sort((a, b) => {
      const revisionA = parseInt(a.split('-').pop() || '0');
      const revisionB = parseInt(b.split('-').pop() || '0');
      return revisionB - revisionA; // Descending order (newest first)
    });

    const mostRecentKey = sortedKeys[0];

    return imageContentUrls[mostRecentKey];
  };

  // Create stable references for components to prevent unnecessary re-renders
  const imageComponentsSignature = useMemo(() => {
    if (!currentPageData?.components) return '';
    const imageComponents = currentPageData.components.filter(
      (component: any) => component.type === 'image' && component.imageReference
    );
    return imageComponents
      .map((c: any) => `${c.id}-${c.imageReference}`)
      .join(',');
  }, [currentPageData?.components]);

  const richMediaComponentsSignature = useMemo(() => {
    if (!currentPageData?.components) return '';
    const richMediaComponents = currentPageData.components.filter(
      (component: any) =>
        component.type === 'rich_media' || component.type === 'content_card'
    );
    return richMediaComponents.map((c: any) => c.id).join(',');
  }, [currentPageData?.components]);

  // True once every rich media / content card component's real content has
  // either been fetched or failed to fetch, so we never flash the empty
  // state banner while the content request is still in flight.
  const richMediaContentReady = useMemo(() => {
    if (!currentPageData?.components) return true;
    const richMediaComponents = currentPageData.components.filter(
      (component: any) =>
        component.type === 'rich_media' || component.type === 'content_card'
    );
    if (richMediaComponents.length === 0) return true;

    return richMediaComponents.every((component: any) => {
      const key = `${currentPageData.id}-${component.id}`;
      return (
        !!customPagesState.richMediaContent[key] ||
        !!customPagesState.richMediaFetchError
      );
    });
  }, [
    currentPageData?.id,
    currentPageData?.components,
    customPagesState.richMediaContent,
    customPagesState.richMediaFetchError,
  ]);

  useEffect(() => {
    if (customPageId) {
      if (currentPageData) {
        setLoading(false);
        setError(null);
      } else if (customPages.length > 0) {
        setError('Page not found');
        setLoading(false);
      } else {
        setLoading(true);
        setError(null);
      }
    }
  }, [customPageId, customPages, currentPageData]);

  useEffect(() => {
    if (pageData) {
      setLoading(false);
      setError(null);
    }
  }, [pageData]);

  useEffect(() => {
    if (
      currentPageData &&
      masterId &&
      (latestRevision != null || revision != null)
    ) {
      const imageComponents = currentPageData.components.filter(
        (component: any) =>
          component.type === 'image' && component.imageReference
      );

      if (imageComponents.length > 0) {
        const currentRevision = latestRevision || revision;

        const uncachedComponents = imageComponents.filter((component: any) => {
          // Check current revision first
          const croppedCached = isImageUrlCached(
            { customPages: { imageContentUrls } },
            currentPageData.id,
            component.id,
            false,
            currentRevision
          );
          const originalCached = isImageUrlCached(
            { customPages: { imageContentUrls } },
            currentPageData.id,
            component.id,
            true,
            currentRevision
          );

          // If not cached for current revision, check if cached for previous revision
          if (!croppedCached || !originalCached) {
            // Look for any cached version with different revision
            const allCacheKeys = Object.keys(imageContentUrls);
            const componentCacheKeys = allCacheKeys.filter((key) =>
              key.includes(`${currentPageData.id}-${component.id}-`)
            );

            // If we have cached versions for different revisions, consider them as cached
            if (componentCacheKeys.length > 0) {
              return false; // Don't fetch since we have cached version with different revision
            }
          }

          return !croppedCached || !originalCached;
        });

        // Only fetch images that are not already cached
        if (uncachedComponents.length > 0) {
          dispatch(
            handleGetAllCustomPageImageContent(
              masterId,
              currentPageData.id,
              currentRevision
            )
          );
        }
      }
    }
    // eslint-disable-next-line
  }, [
    currentPageData?.id,
    imageComponentsSignature,
    masterId,
    latestRevision,
    revision,
    dispatch,
    imageContentUrls,
  ]);

  useEffect(() => {
    if (currentPageData && masterId) {
      const richMediaComponents = currentPageData.components.filter(
        (component: any) =>
          component.type === 'rich_media' || component.type === 'content_card'
      );

      if (richMediaComponents.length > 0) {
        // Only fetch rich media content that is not already cached (following image content pattern)
        const uncachedComponents = richMediaComponents.filter(
          (component: any) => {
            const key = `${currentPageData.id}-${component.id}`;
            const cachedContent = customPagesState.richMediaContent[key];
            return !cachedContent;
          }
        );

        // Only make API calls for components that don't have cached content
        if (uncachedComponents.length > 0) {
          uncachedComponents.forEach((component: any) => {
            dispatch(
              getCustomPageRichMediaContentAction(
                masterId,
                currentPageData.id,
                component.id
              )
            );
          });
        }
      }
    }
    // eslint-disable-next-line
  }, [
    currentPageData?.id,
    richMediaComponentsSignature,
    masterId,
    dispatch,
    customPagesState.richMediaContent,
  ]);

  // Helper function to check if a single component has content
  const componentHasContent = (component: any): boolean => {
    switch (component.type) {
      case 'rich_media':
        return component.content && component.content.trim() !== '';
      case 'image':
        const hasImageReference =
          component.imageReference && component.imageReference.trim() !== '';
        const hasImageUrl =
          component.imageUrl && component.imageUrl.trim() !== '';

        let imageUrlFromState = '';
        if (hasImageReference && currentPageData?.id) {
          imageUrlFromState = getImageUrlFromCacheFlexible(
            currentPageData.id,
            component.id,
            false,
            latestRevision || revision
          );
        }

        return (
          hasImageUrl ||
          hasImageReference ||
          (imageUrlFromState && imageUrlFromState.trim() !== '')
        );
      case 'content_card':
        return (
          (component.title && component.title.trim() !== '') ||
          (component.content && component.content.trim() !== '')
        );
      default:
        return false;
    }
  };

  const handleMoveUp = async (index: number) => {
    if (currentPageData?.id && masterId && index > 0) {
      try {
        await dispatch(
          moveSectionUpWithAPI(masterId, currentPageData.id, index)
        );
      } catch (error) {
        console.error('Error moving section up:', error);
      }
    }
  };

  const handleMoveDown = async (index: number) => {
    if (
      currentPageData?.id &&
      masterId &&
      currentPageData?.components &&
      index < currentPageData.components.length - 1
    ) {
      try {
        await dispatch(
          moveSectionDownWithAPI(masterId, currentPageData.id, index)
        );
      } catch (error) {
        console.error('Error moving section down:', error);
      }
    }
  };

  const renderComponent = (component: any, index: number) => {
    const moveProps = {
      onMoveUp: () => handleMoveUp(index),
      onMoveDown: () => handleMoveDown(index),
      hideUp: index === 0,
      hideDown: index === (currentPageData?.components?.length || 0) - 1,
      showControls:
        isEditMode && (currentPageData?.components?.length || 0) > 1,
    };

    switch (component.type) {
      case 'image':
        return renderImageComponent(component, moveProps);
      case 'rich_media':
        return renderTextComponent(component, moveProps);
      case 'content_card':
        return renderContentCardComponent(component, moveProps);
      default:
        return (
          <div key={component.id} className={styles.unknownComponent}>
            Unknown component type: {component.type}
          </div>
        );
    }
  };

  const renderImageComponent = (component: any, moveProps?: any) => {
    const imageUrl =
      component.imageUrl ||
      (component.imageReference && currentPageData?.id
        ? getImageUrlFromCacheFlexible(
            currentPageData.id,
            component.id,
            false,
            latestRevision || revision
          )
        : '');
    const originalImage =
      component.originalImage ||
      (component.imageReference && currentPageData?.id
        ? getImageUrlFromCacheFlexible(
            currentPageData.id,
            component.id,
            true,
            latestRevision || revision
          )
        : '');

    return (
      <ImageComponent
        key={component.id}
        id={component.id}
        imageUrl={imageUrl}
        originalImage={originalImage}
        imageReference={component.imageReference}
        cropArea={component.cropArea}
        customPageId={currentPageData?.id}
        onSave={(
          id: string,
          newImageUrl: string,
          newOriginalImage: string,
          croppedArea: any
        ) => {
          handleImageUpdate(id, newImageUrl, newOriginalImage, croppedArea);
        }}
        onDelete={(id: string) => {
          handleComponentRemove(id);
        }}
        moveProps={moveProps}
      />
    );
  };

  if (loading || (!isEditMode && !richMediaContentReady)) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorBoundary
        lineOne={getText(CUSTOM_PAGE_NOT_FOUND, language)}
        lineTwo={''}
      />
    );
  }

  if (!currentPageData) {
    return (
      <div className={styles.errorContainer}>
        <Alert
          message="Page Not Found"
          description="The requested custom page could not be found."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  const renderTextComponent = (component: any, moveProps?: any) => {
    return (
      <TextComponent
        key={component.id}
        id={component.id}
        content={component.content}
        customPageId={currentPageData.id}
        onSave={(id: string, content: string) => {
          handleTextUpdate(id, content);
        }}
        onDelete={(id: string) => {
          handleComponentRemove(id);
        }}
        moveProps={moveProps}
      />
    );
  };

  const renderContentCardComponent = (component: any, moveProps?: any) => {
    return (
      <ContentCard
        key={component.id}
        id={component.id}
        title={component.title}
        content={component.content}
        customPageId={currentPageData.id}
        onSave={(id: string, title: string, content: string) => {
          handleContentCardUpdate(id, title, content);
        }}
        onDelete={(id: string) => {
          handleComponentRemove(id);
        }}
        moveProps={moveProps}
      />
    );
  };

  // Determine which page to navigate to after deletion
  const getNavigationPageAfterDeletion = (
    currentPageId: string,
    allPages: CustomPageType[]
  ): CustomPageType | null => {
    const currentPageIndex = allPages.findIndex(
      (page) => page.id === currentPageId
    );

    if (currentPageIndex === -1 || allPages.length <= 1) {
      return null;
    }

    // If deleting the first page, navigate to the next page
    if (currentPageIndex === 0) {
      return allPages[1];
    }

    // For any other page (middle or last), navigate to the previous page
    return allPages[currentPageIndex - 1];
  };

  const handleDeletePage = async () => {
    try {
      if (!masterId || !customPageId) {
        console.error('Missing required parameters for deletion:', {
          masterId,
          customPageId,
        });
        return;
      }

      // Determine navigation target before deletion
      const navigationPage = getNavigationPageAfterDeletion(
        customPageId,
        customPages
      );

      await deleteCustomPage(masterId, customPageId, dispatch);
      setShowDeleteModal(false);

      let navigationPath: string;
      if (navigationPage) {
        // Navigate to the determined page
        navigationPath = `/${hash}${
          employerName ? '/' + employerName : ''
        }/custom-page/${navigationPage.id}${
          previewToken ? `?previewToken=${previewToken}` : ''
        }`;
      } else {
        // Navigate to home page if no custom pages remain
        navigationPath = `/${hash}${employerName ? '/' + employerName : ''}${
          previewToken ? `?previewToken=${previewToken}` : ''
        }`;
      }

      navigate(navigationPath);
    } catch (error) {
      console.error('Error deleting page:', error);
    }
  };

  const handleAddComponents = async (
    pageTitle: string,
    selectedComponents: any[]
  ) => {
    try {
      if (!currentPageData?.id || !masterId) {
        console.error('Missing page ID or master ID');
        return;
      }

      await addComponentsToPage(
        masterId,
        currentPageData.id,
        selectedComponents,
        dispatch,
        () => ({ customPages: customPagesState })
      );

      setShowAddComponentModal(false);
    } catch (error) {
      console.error('Error adding components:', error);
    }
  };

  const handleTextUpdate = (componentId: string, content: string) => {
    if (currentPageData && masterId) {
      dispatch(
        updateCustomPageRichMediaContentAction(
          masterId,
          currentPageData.id,
          componentId,
          content,
          '',
          'RICH_MEDIA'
        )
      );
    }
  };

  const handleContentCardUpdate = (
    componentId: string,
    title: string,
    content: string
  ) => {
    if (currentPageData && masterId) {
      dispatch(
        updateCustomPageRichMediaContentAction(
          masterId,
          currentPageData.id,
          componentId,
          content,
          title,
          'CONTENT_CARD'
        )
      );
    }
  };

  const handleImageUpdate = async (
    componentId: string,
    newImageUrl: string,
    newOriginalImage: string,
    croppedArea: any
  ) => {
    if (currentPageData && masterId) {
      try {
        const cropParams = {
          x: croppedArea.x,
          y: croppedArea.y,
          width: croppedArea.width,
          height: croppedArea.height,
        };

        await dispatch(
          handleCustomPageImageUpload(
            masterId,
            newImageUrl,
            currentPageData.id,
            componentId,
            newOriginalImage,
            cropParams
          )
        );
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
  };

  const handleComponentRemove = async (componentId: string) => {
    if (currentPageData) {
      const componentToRemove = currentPageData.components.find(
        (comp) => comp.id === componentId
      );

      if (componentToRemove) {
        try {
          let response;
          if (componentToRemove.type === 'image') {
            response = await dispatch(
              handleCustomPageImageRemoval(
                masterId,
                currentPageData.id,
                componentId
              )
            );
          } else {
            response = await dispatch(
              handleCustomPageSectionDeletion(
                masterId,
                currentPageData.id,
                componentId
              )
            );
          }
          if (response.latestRevision) {
            dispatch(updateBenguideLatestRevision(response.latestRevision));
          }
        } catch (error) {
          console.error('Error removing component:', error);
        }
      }
    }
  };

  return (
    <div className={styles.customPageContainer}>
      <div className={styles.pageHeader}>
        <img src={title} alt="edit" />
        <h1 className={styles.pageTitle}>{currentPageData.title}</h1>
        {isEditMode && (
          <div className={styles.headerButtons}>
            <Button
              type="link"
              danger
              onClick={() => setShowDeleteModal(true)}
              className={styles.deleteButton}
            >
              Delete Page
            </Button>
            <Button
              type="primary"
              onClick={() => setShowAddComponentModal(true)}
              className={styles.addComponentButton}
            >
              + Add Components
            </Button>
          </div>
        )}
      </div>

      <div className={styles.pageContent}>
        {(() => {
          if (
            currentPageData.components &&
            currentPageData.components.length > 0
          ) {
            if (!isEditMode) {
              // In preview mode, only render components that have content
              const componentsWithContent = currentPageData.components.filter(
                (component: any) => componentHasContent(component)
              );

              // If no components have content, show the empty state message
              if (componentsWithContent.length === 0) {
                return (
                  <ErrorBoundary
                    lineOne={getText(CUSTOM_PAGE_SECTION_NOT_FOUND, language)}
                    lineTwo={getText(
                      CUSTOM_PAGE_SECTION_NOT_FOUND_MORE_INFO,
                      language
                    )}
                  />
                );
              }

              // Render only the components that have content
              return componentsWithContent.map((component, index) =>
                renderComponent(component, index)
              );
            } else {
              // In edit mode, render all components (existing behavior)
              return currentPageData.components.map((component, index) =>
                renderComponent(component, index)
              );
            }
          } else {
            if (!isEditMode) {
              return (
                <ErrorBoundary
                  lineOne={getText(CUSTOM_PAGE_SECTION_NOT_FOUND, language)}
                  lineTwo={getText(
                    CUSTOM_PAGE_SECTION_NOT_FOUND_MORE_INFO,
                    language
                  )}
                />
              );
            } else {
              return (
                <NoCustomPageSectionsView
                  title="No components added yet"
                  subheader="Click 'Add Components' to get started"
                />
              );
            }
          }
        })()}
      </div>

      <RemoveCustomPageModal
        visible={showDeleteModal}
        pageTitle={currentPageData.title}
        onClose={() => setShowDeleteModal(false)}
        onRemove={handleDeletePage}
      />

      <AddCustomPageComponent
        visible={showAddComponentModal}
        onClose={() => setShowAddComponentModal(false)}
        onSave={handleAddComponents}
        textLimit={22}
        isAddingComponent={true}
        existingPageTitle={currentPageData.title}
      />
      <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
    </div>
  );
};

CustomPage.propTypes = {
  pageData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    components: PropTypes.array.isRequired,
  }),
};

CustomPage.defaultProps = {
  pageData: undefined,
};

CustomPage.defaultProps = {
  pageData: undefined,
  customPagesList: [],
};

export default CustomPage;
